/* src/components/dashboard/FocusMeter.js */

import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons'; // Removed unused icons faTrophy, faChevronRight
import { DIFFICULTY_TIERS } from '../../constants/gameConstants';
import { getTierFromDifficulty, formatCombinedDifficulty } from '../../contexts/gameHelpers';

/**
 * Enhanced Focus Meter component displays current difficulty level with a visual meter
 * showing distinct tier segments and a unified gradient progress bar.
 * Includes smooth animations and better visual feedback while matching site styles.
 *
 * @param {number} difficulty - The current difficulty level (0.00-0.95)
 * @param {number} nBack - The current n-back level
 * @param {boolean} [showAdvancementInfo=true] - Whether to display the advancement information (DEPRECATED: Use hideAdvancementInfo)
 * @param {boolean} [hideAdvancementInfo=false] - Whether to hide the advancement information section
 * @param {boolean} [hideCurrentLevelDisplay=false] - Whether to hide the 'Current Level: X.XX' text
 * @param {boolean} [hideTierDisplay=false] - Whether to hide the 'N-back Tier' text
 */
const FocusMeter = ({
  difficulty,
  nBack = 2,
  showAdvancementInfo = true, // Keep for potential backward compatibility, but prioritize hideAdvancementInfo
  hideAdvancementInfo = false,
  hideCurrentLevelDisplay = false,
  hideTierDisplay = false
 }) => {
  const [markerPosition, setMarkerPosition] = useState(0);
  const tier = getTierFromDifficulty(difficulty);
  const MAX_N_BACK_DISPLAY = 9; // Consistent with ProfileDashboard

  // Use hideAdvancementInfo if provided, otherwise fallback to showAdvancementInfo for compatibility
  const shouldShowAdvancementInfo = hideAdvancementInfo ? false : showAdvancementInfo;

  // Calculate percentage fill
  const progressPercentage = useMemo(() => {
    const MAX_DIFFICULTY_VALUE = 0.95;
    const DIFFICULTY_STEP = 0.05;
    const clampedDifficulty = Math.max(0, Math.min(MAX_DIFFICULTY_VALUE, difficulty ?? 0));
    const step = Math.round(clampedDifficulty / DIFFICULTY_STEP);
    return Math.max(0, Math.min(100, (step * 5)));
  }, [difficulty]);

  // Update marker position with animation
  useEffect(() => {
    const timer = setTimeout(() => {
      setMarkerPosition(progressPercentage);
    }, 50); // Short delay for visual effect
    return () => clearTimeout(timer);
  }, [progressPercentage]);

  // --- Helper Text and Next Level Logic ---
  const nextNBackLevel = nBack < MAX_N_BACK_DISPLAY ? nBack + 1 : nBack;
  const advancementText = nBack < MAX_N_BACK_DISPLAY
    ? `Reach ${nBack}.99 difficulty to progress towards ${nextNBackLevel}-back.`
    : `Master ${nBack}.99 difficulty to complete all levels.`;
  // ---

  // Get tier display name
  const getTierName = (tierObj) => {
    if (!tierObj || !tierObj.name) return 'Unknown'; // Add safety check
    return tierObj.name.charAt(0).toUpperCase() + tierObj.name.slice(1);
  };

  return (
    <div className="mb-4">
      {/* Header Row with conditionally hidden elements */}
      <div className="flex justify-between items-center mb-3">
        {/* Tier Display (Conditionally Hidden) */}
        { !hideTierDisplay ? (
          <div>
            <div className="flex items-center">
              <span className="font-medium text-lg text-gray-800">{nBack}-back</span>
              <span className="ml-2 font-semibold text-indigo-700 px-2 py-0.5 rounded-md bg-indigo-100 text-sm">
                {getTierName(tier)}
              </span>
            </div>
          </div>
        ) : (
          // Use an empty div to maintain space if hidden and current level is shown
          !hideCurrentLevelDisplay ? <div></div> : null
        )}

        {/* Current Level Display (Conditionally Hidden) */}
        { !hideCurrentLevelDisplay ? (
          <div className="bg-gray-100 px-3 py-1.5 rounded-md border border-gray-200">
            <span className="text-sm text-gray-700">Current Level: </span>
            <span className="font-medium text-gray-800 font-mono">{formatCombinedDifficulty(nBack, difficulty)}</span>
          </div>
        ) : (
           // Use an empty div to maintain space if hidden and tier is shown
           !hideTierDisplay ? <div></div> : null
        )}

         {/* Placeholder if both are hidden to prevent layout collapse */}
         { hideTierDisplay && hideCurrentLevelDisplay && <div className="h-8"></div> }
      </div>

      {/* Progress Bar Container */}
      <div className="relative h-4 rounded-full overflow-hidden bg-gray-100 border border-gray-200 shadow-inner">
        {/* Background Segments - More subtle dividers */}
        <div className="absolute inset-0 flex h-full w-full">
          {Object.values(DIFFICULTY_TIERS).map((tierObj, index) => (
            <div
              key={tierObj.name + '-bg'}
              className={`w-1/5 h-full ${index !== 0 ? 'border-l border-white/50' : ''}`}
            />
          ))}
        </div>

        {/* Progress Fill - Consistent gradient color matching the app */}
        <div
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-indigo-400 to-indigo-600 transition-all duration-1000 ease-out"
          style={{ width: `${markerPosition}%` }}
        />

        {/* Tier Boundaries - Subtle white dividers */}
        <div className="absolute inset-0 flex pointer-events-none">
          {[1, 2, 3, 4].map(i => (
            <div
              key={`boundary-${i}`}
              className="absolute h-full w-px bg-white/70"
              style={{ left: `${i * 20}%` }}
            />
          ))}
        </div>

        {/* Position Marker - Subtle indicator */}
        <div
          className="absolute top-0 h-full w-1 bg-white/70 transition-all duration-1000 ease-out"
          style={{
            left: `${markerPosition}%`,
            transform: 'translateX(-50%)'
          }}
          aria-hidden="true"
        />
      </div>

      {/* Tier Labels Below Progress Bar */}
      <div className="flex justify-between text-xs mt-2 px-1">
        {Object.values(DIFFICULTY_TIERS).map((tierObj) => {
          const epsilon = 0.001; // Tolerance for floating point comparisons
          // Ensure difficulty is treated as 0 if null/undefined
          const currentDifficulty = difficulty ?? 0;
          const isInRange = currentDifficulty >= (tierObj.min - epsilon) && currentDifficulty <= (tierObj.max + epsilon);
          return (
            <div
              key={tierObj.name + '-label'}
              className="w-1/5 text-center flex flex-col items-center"
            >
              <span className={`${isInRange ? 'text-indigo-700 font-semibold' : 'text-gray-400'}`}>
                {getTierName(tierObj)}
              </span>
            </div>
          );
        })}
      </div>

      {/* Advancement Info (Conditionally Hidden) */}
      { shouldShowAdvancementInfo && ( // Use the derived variable
        <div className="relative text-center mt-2">
          {/* Button to toggle the tooltip */}
          <button
            className="inline-flex items-center text-xs text-gray-500 px-2 py-1 rounded-md bg-gray-50 border border-gray-200 hover:bg-gray-100 transition-colors"
            aria-label="Show advancement info"
            onClick={(e) => {
              const tooltip = e.currentTarget.nextElementSibling;
              if (tooltip) {
                // Toggle visibility
                const isHidden = tooltip.classList.contains('hidden');
                tooltip.classList.toggle('hidden', !isHidden);

                // Add/remove click outside listener only if tooltip becomes visible
                if (isHidden) {
                  // Use a timeout to ensure the click handler doesn't fire immediately from the button click itself
                  setTimeout(() => {
                    const clickHandler = (event) => {
                      // Check if the click was outside the tooltip AND outside the button
                      if (!tooltip.contains(event.target) && event.target !== e.currentTarget) {
                        tooltip.classList.add('hidden');
                        document.removeEventListener('click', clickHandler);
                      }
                    };
                    document.addEventListener('click', clickHandler);
                  }, 0);
                }
                // No need to explicitly remove listener here if hiding, as it should remove itself after one outside click
              }
            }}
          >
            <FontAwesomeIcon icon={faCircleInfo} className="text-gray-500 mr-1.5" aria-hidden="true" />
            Advancement Info
          </button>
          {/* The Tooltip itself */}
          <div className="hidden absolute z-10 mt-2 p-3 bg-white rounded-md shadow-lg border border-gray-200 text-sm text-gray-700 text-left left-1/2 transform -translate-x-1/2 w-64">
            {advancementText}
            {/* Additional details shown inside the tooltip */}
            {nBack < MAX_N_BACK_DISPLAY && (
              <div className="mt-2 pt-2 border-t border-gray-100">
                 <div className="flex items-center text-xs text-gray-500">
                     <span className="inline-block w-2 h-2 bg-gray-300 rounded-full mr-1.5"></span>
                     <span>Current: {formatCombinedDifficulty(nBack, difficulty)}</span>
                 </div>
                 <div className="flex items-center text-xs text-gray-500 mt-1">
                     <span className="inline-block w-2 h-2 bg-indigo-400 rounded-full mr-1.5"></span>
                     <span>Target: {formatCombinedDifficulty(nBack, 0.99)}</span>
                 </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FocusMeter;